'use client';

import path from 'path';

import { AssetPath, SCENE_NAME_EXT } from '@gnsx/genesys.js';
import { ActivityIcon, CodeIcon, ImageOffIcon, LayoutGridIcon, LogOutIcon, MapIcon, MaximizeIcon, MountainIcon, NewspaperIcon, PackageCheckIcon, PackageIcon, PackagePlusIcon, SaveIcon, SettingsIcon, Trash2Icon, WrenchIcon } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { usePathname } from 'next/navigation';
import { useCallback, useEffect, useMemo, useState } from 'react';


import { AboutDialog } from '../AboutDialog';
import { BrowseAssetPacksDialog } from '../BrowseAssetPacksDialog';
import { ExportAssetPackDialog } from '../ExportAssetPackDialog';
import { LoadUrlDialog } from '../LoadUrlDialog';
import { NewAssetPackDialog } from '../NewAssetPackDialog';
import { WhatsNewDialog } from '../WhatsNewDialog';

import { TitleBarButton } from './TitleBarButton';

import { assistantStore } from '@/app/editor/ai-assistant/assistantStore';
import { ModelViewerDialog } from '@/app/editor/components/dialogs/ModelViewerDialog';
import { UploadProjectDialog } from '@/app/editor/components/dialogs/UploadProjectDialog';
import { ProjectSettingsDialog } from '@/app/editor/components/layout/ProjectSettingsDialog';
import { type NewSceneOptions, SaveSceneDialog } from '@/app/editor/components/layout/SaveSceneDialog';
import { NavMeshDialog } from '@/app/editor/components/navmesh/NavMeshDialog';
import { editorActions } from '@/app/editor/core';
import { removeImportedMeshCombinations } from '@/app/editor/core/actions/sceneActions';
import { saveScene } from '@/app/editor/core/sceneHandler';
import { requestManualUpdateCheck } from '@/app/editor/hooks/useInitApp';
import { useNewScene } from '@/app/editor/hooks/useNewScene';
import { appPreferencesStore } from '@/app/global/appPreferencesStore';
import { appInfo, getAppInfo, platform, sdk } from '@/app/sdk';
import { IconBuild, IconCursor, IconDevTools, IconFolder1, IconHelp, IconMenu, IconPlay, IconReload, IconUpload } from '@/components/icons';
import { DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuGroup, DropdownMenuGroupLabel, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuShortcut, DropdownMenuSub, DropdownMenuSubContent, DropdownMenuSubTrigger, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { EDITOR_VERSION, PRODUCT_NAME, PROJECT_ASSETS_FOLDER } from '@/const';
import { useAuth } from '@/context/AuthContext';
import { buildProject, editorSave, uploadProject } from '@/editor/core/actions/editorActions';
import { PlayLaunchMode } from '@/editor/core/play-preview/playPreviewTypes';
import { project, sceneStore, store } from '@/editor/store';
import { editorStore, PlayModeSceneType } from '@/editor/store/editorStore';
import { clearIndexedDbScreenshotsForCurrentProject } from '@/editor/store/sceneStore';
import { getCtrlOrCmdKeyModifier, IS_DEV } from '@/lib/utils';

type DialogKey = 'projectSettings' | 'lightmapBaking' | 'navMesh' | 'loadUrl' | 'about' | 'whatsNew' | 'browseAssetPacks' | 'newAssetPack' | 'exportAssetPack';

interface DialogState {
  projectSettings: boolean;
  lightmapBaking: boolean;
  navMesh: boolean;
  loadUrl: boolean;
  about: boolean;
  whatsNew: boolean;
  browseAssetPacks: boolean;
  newAssetPack: boolean;
  exportAssetPack: boolean;
}

const INITIAL_DIALOG_STATE: DialogState = {
  projectSettings: false,
  lightmapBaking: false,
  navMesh: false,
  loadUrl: false,
  about: false,
  whatsNew: false,
  browseAssetPacks: false,
  newAssetPack: false,
  exportAssetPack: false,
};

type AppSettingsMenuItemsProps = {
  askBeforeProjectUpdate: boolean;
  onAskBeforeProjectUpdateChange: (checked: boolean) => void;
};

const AppSettingsMenuItems = observer(({
  askBeforeProjectUpdate,
  onAskBeforeProjectUpdateChange,
}: AppSettingsMenuItemsProps) => {
  const handleAiAssistantEnabledChange = useCallback((checked: boolean) => {
    if (!checked) {
      editorStore.setAgentChatOpen(false);
      assistantStore.detach();
    }
    void appPreferencesStore.setAiAssistantEnabled(checked);
  }, []);

  const handleGenesysMcpEnabledChange = useCallback((checked: boolean) => {
    void appPreferencesStore.setGenesysMcpEnabled(checked);
  }, []);

  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger>
        <SettingsIcon className="size-4" />
        App Settings
      </DropdownMenuSubTrigger>
      <DropdownMenuSubContent>
        <DropdownMenuCheckboxItem
          checked={appPreferencesStore.aiAssistantEnabled}
          onCheckedChange={handleAiAssistantEnabledChange}
          showAsCheckbox
          checkboxPosition="right"
          onSelect={e => e.preventDefault()}
        >
          Genesys AI Assistant
        </DropdownMenuCheckboxItem>
        <DropdownMenuCheckboxItem
          checked={appPreferencesStore.genesysMcpEnabled}
          onCheckedChange={handleGenesysMcpEnabledChange}
          showAsCheckbox
          checkboxPosition="right"
          onSelect={e => e.preventDefault()}
        >
          Genesys MCP
        </DropdownMenuCheckboxItem>
        <DropdownMenuSeparator />
        <DropdownMenuCheckboxItem
          checked={askBeforeProjectUpdate}
          onCheckedChange={onAskBeforeProjectUpdateChange}
          showAsCheckbox
          checkboxPosition="right"
          onSelect={e => e.preventDefault()}
        >
          Ask before project update
        </DropdownMenuCheckboxItem>
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  );
});
AppSettingsMenuItems.displayName = 'AppSettingsMenuItems';

/** Title bar menu component */
export const TitleBarMenu = observer(() => {
  const pathname = usePathname();
  const { firebaseUser } = useAuth();
  const editorOpened = pathname.startsWith('/editor');
  const projectLoaded = project.isProjectOpened;
  const buildingProject = store.isBuildingProject;
  const isPlayMode = sceneStore.isPlayMode;
  const isPlayModeStarting = sceneStore.isPlayModeStarting;

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [dialogs, setDialogs] = useState<DialogState>(INITIAL_DIALOG_STATE);
  const [saveDialogMode, setSaveDialogMode] = useState<'saveAs' | 'newScene' | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [aboutAppInfo, setAboutAppInfo] = useState<{ appVersion: string; engineVersion: string; isDev: boolean } | null>(null);
  const [askBeforeProjectUpdate, setAskBeforeProjectUpdate] = useState(false);

  const { handleNewScene: triggerNewScene, handleConfirmNewScene } = useNewScene();

  const ctrlOrCmd = getCtrlOrCmdKeyModifier(platform);

  // Consolidated disabled states
  const isEditorBusy = !projectLoaded || buildingProject || isSaving;
  const isEditorDisabled = isEditorBusy || isPlayMode;
  const isAuthRequired = isEditorDisabled || !firebaseUser;
  const thumbnailService = editorStore.thumbnailService;

  const openDialog = useCallback((key: DialogKey) => setDialogs(prev => ({ ...prev, [key]: true })), []);
  const closeDialog = useCallback((key: DialogKey) => setDialogs(prev => ({ ...prev, [key]: false })), []);

  useEffect(() => {
    let disposed = false;

    const loadAppSettings = async () => {
      if (!sdk) {
        return;
      }

      try {
        const storedAskBeforeProjectUpdate = await sdk.app.getState('app.askBeforeProjectUpdate');
        if (!disposed) {
          setAskBeforeProjectUpdate(storedAskBeforeProjectUpdate === true);
        }
      } catch (error) {
        console.error('Failed to load app settings:', error);
      }
    };

    void loadAppSettings();

    return () => {
      disposed = true;
    };
  }, []);

  const handleAskBeforeProjectUpdateChange = useCallback((checked: boolean) => {
    setAskBeforeProjectUpdate(checked);

    if (!sdk) {
      return;
    }

    void sdk.app.setState('app.askBeforeProjectUpdate', checked).catch((error) => {
      console.error('Failed to save app settings:', error);
    });
  }, []);

  const handleNewScene = useCallback(() => {
    setSaveDialogMode('newScene');
    triggerNewScene();
  }, [triggerNewScene]);

  const handleSave = useCallback(async () => {
    if (editorStore.project.currentScene) {
      await editorSave(true);
    } else {
      setSaveDialogMode('saveAs');
    }
  }, []);

  const handleSaveAs = useCallback(() => setSaveDialogMode('saveAs'), []);

  const handleSaveSceneWithName = useCallback(async (newSceneName: string) => {
    let scenePath = path.join(PROJECT_ASSETS_FOLDER, newSceneName);
    if (!scenePath.endsWith(SCENE_NAME_EXT)) scenePath += SCENE_NAME_EXT;
    setIsSaving(true);
    try {
      await saveScene(scenePath ? AssetPath.fromString(scenePath) : undefined);
    } catch (error) {
      console.error('Failed to save scene:', error);
    } finally {
      setIsSaving(false);
    }
  }, []);

  const handleSaveDialogSubmit = useCallback(async (sceneName: string, options?: NewSceneOptions) => {
    if (saveDialogMode === 'newScene') await handleConfirmNewScene(sceneName, options);
    else if (saveDialogMode === 'saveAs') await handleSaveSceneWithName(sceneName);
  }, [saveDialogMode, handleConfirmNewScene, handleSaveSceneWithName]);

  const handleShowInExplorer = useCallback(() => sdk?.os.openPath(project.projectFolder), []);
  const handleOpenInCodeEditor = useCallback(() => {
    const workspaceFile = `${project.projectFolder}/${project.projectName}.code-workspace`;
    sdk?.os.openFile(workspaceFile);
  }, []);
  const handleCheckForUpdates = useCallback(() => {
    void requestManualUpdateCheck();
  }, []);
  const handleOpenAppLog = useCallback(async () => {
    const info = await getAppInfo();
    info && sdk?.os.openPath(info.logPath);
  }, []);
  const handleDevLoadUrlSubmit = useCallback((url: string) => sdk?.dev.loadUrl(url), []);
  const handleToggleDevConsole = useCallback(() => sdk?.tools.toggleDevConsole(), []);
  const handleReload = useCallback((force = true) => {
    if (sdk) {
      sdk.ui.reload(force);
      return;
    }
    window.location.reload();
  }, []);
  const handleToggleFullScreen = useCallback(() => {
    if (document.fullscreenElement) document.exitFullscreen();
    else document.documentElement.requestFullscreen();
  }, []);

  // DropdownMenuShortcut is display-only; mirror editor reload hotkeys on non-editor routes.
  useEffect(() => {
    if (editorOpened) {
      return;
    }

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.repeat) {
        return;
      }

      const target = e.target;
      if (
        target instanceof HTMLInputElement
        || target instanceof HTMLTextAreaElement
        || target instanceof HTMLSelectElement
      ) {
        return;
      }
      if (target instanceof HTMLElement && target.isContentEditable) {
        return;
      }

      if (!(e.ctrlKey || e.metaKey) || e.key.toLowerCase() !== 'r') {
        return;
      }

      e.preventDefault();
      handleReload(true);
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [editorOpened, handleReload]);

  const handleClearThumbnails = useCallback(async () => {
    const thumbnailService = editorStore.thumbnailService;
    if (!thumbnailService) {
      return;
    }
    try {
      await thumbnailService.clearAllStoredThumbnails();
    } catch (error) {
      console.error('Failed to clear thumbnails:', error);
    }
    try {
      await clearIndexedDbScreenshotsForCurrentProject();
    } catch (error) {
      console.error('Failed to clear IndexedDB screenshots:', error);
    }
  }, []);

  const handleShowAbout = useCallback(async () => {
    const info = await getAppInfo();
    if (info) {
      setAboutAppInfo(info);
      openDialog('about');
    }
  }, [openDialog]);

  const appSettingsMenu = useMemo(() => (
    <AppSettingsMenuItems
      askBeforeProjectUpdate={askBeforeProjectUpdate}
      onAskBeforeProjectUpdateChange={handleAskBeforeProjectUpdateChange}
    />
  ), [askBeforeProjectUpdate, handleAskBeforeProjectUpdateChange]);

  const projectMenuItems = useMemo(() => (
    <>
      {/* <DropdownMenuGroupLabel>{project.projectName}</DropdownMenuGroupLabel> */}
      <DropdownMenuGroup>
        {isPlayMode && (
          <>
            <DropdownMenuItem
              onClick={() => void editorActions.setPlayModeActive(false)}
              disabled={isPlayModeStarting}
            >
              <LogOutIcon className="size-4" />
              Exit Play Mode
              <DropdownMenuShortcut>{ctrlOrCmd}+P</DropdownMenuShortcut>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
          </>
        )}
        <DropdownMenuItem onClick={handleNewScene} disabled={isEditorDisabled}>
          <MountainIcon className="size-4" />
          New Scene
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handleSave} disabled={isAuthRequired}>
          <SaveIcon className="size-4" />
          Save Scene
          <DropdownMenuShortcut>{ctrlOrCmd}+S</DropdownMenuShortcut>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handleSaveAs} disabled={isAuthRequired}>
          <SaveIcon className="size-4" />
          Save Scene As
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => buildProject()} disabled={!projectLoaded || buildingProject || isPlayMode}>
          <IconBuild className="size-4" />
          Build Project
          <DropdownMenuShortcut>{ctrlOrCmd}+B</DropdownMenuShortcut>
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => {
            editorStore.setPlayModeSceneType(PlayModeSceneType.GameDefault);
            void editorActions.setPlayModeActive(true);
          }}
          disabled={isEditorBusy || !firebaseUser}
        >
          <IconPlay className="size-4" />
          Play Game Default Scene
          <DropdownMenuShortcut>F5</DropdownMenuShortcut>
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => {
            editorStore.setPlayModeSceneType(PlayModeSceneType.CurrentScene);
            void editorActions.setPlayModeActive(true);
          }}
          disabled={isEditorBusy || !firebaseUser}
        >
          <IconPlay className="size-4" />
          Play Current Scene
          <DropdownMenuShortcut>F6</DropdownMenuShortcut>
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => {
            editorStore.setPlayLaunchMode(PlayLaunchMode.NewWindow);
            void editorActions.setPlayModeActive(true, { launchMode: PlayLaunchMode.NewWindow });
          }}
          disabled={isEditorBusy || !firebaseUser}
        >
          <IconPlay className="size-4" />
          Play in New Window
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => {
            editorStore.setPlayLaunchMode(PlayLaunchMode.MobilePreview);
            void editorActions.setPlayModeActive(true, { launchMode: PlayLaunchMode.MobilePreview });
          }}
          disabled={isEditorBusy || !firebaseUser}
        >
          <IconPlay className="size-4" />
          Play in Mobile Preview
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => openDialog('projectSettings')} disabled={!projectLoaded || isPlayMode}>
          <SettingsIcon className="size-4" />
          Project Settings
        </DropdownMenuItem>
        {sdk && appSettingsMenu}
        <DropdownMenuItem onClick={uploadProject} disabled={!projectLoaded || buildingProject || isPlayMode}>
          <IconUpload className="size-4" />
          Upload Project
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuSub>
          <DropdownMenuSubTrigger>
            <span className="flex items-center gap-2"><WrenchIcon className="size-4" /> Tools</span>
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            <DropdownMenuItem onClick={() => openDialog('newAssetPack')} disabled={isEditorDisabled}>
              <PackagePlusIcon className="size-4" />
              New Asset Pack...
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => openDialog('exportAssetPack')} disabled={isEditorDisabled}>
              <PackageCheckIcon className="size-4" />
              Export Asset Pack...
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => openDialog('browseAssetPacks')} disabled={isEditorDisabled}>
              <PackageIcon className="size-4" />
              Browse Asset Packs
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => openDialog('navMesh')} disabled={isEditorDisabled}>
              <MapIcon className="size-4" />
              Generate NavMesh
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => removeImportedMeshCombinations()} disabled={isAuthRequired}>
              <Trash2Icon className="size-4" />
              Remove Imported Mesh Combinations
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => { void handleClearThumbnails(); }}
              disabled={isEditorDisabled || !thumbnailService}
            >
              <ImageOffIcon className="size-4" />
              Clear Thumbnails
            </DropdownMenuItem>
            <DropdownMenuCheckboxItem
              checked={editorStore.showPerformanceMonitor}
              onCheckedChange={editorStore.setShowPerformanceMonitor}
              showAsCheckbox={true}
              checkboxPosition="right"
              onSelect={e => e.preventDefault()}
            >
              <ActivityIcon className="size-4" />
              Performance Monitor
            </DropdownMenuCheckboxItem>
            {IS_DEV && (
              <DropdownMenuCheckboxItem
                checked={editorStore.isDevPanelOpen}
                onCheckedChange={editorStore.setDevPanelOpen}
                showAsCheckbox={true}
                checkboxPosition="right"
                onSelect={e => e.preventDefault()}
              >
                <CodeIcon className="size-4" />
                DevPanel
              </DropdownMenuCheckboxItem>
            )}
          </DropdownMenuSubContent>
        </DropdownMenuSub>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={handleShowInExplorer}>
          <IconFolder1 className="size-4" />
          Show in Explorer
          <DropdownMenuShortcut>{ctrlOrCmd}+E</DropdownMenuShortcut>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handleOpenInCodeEditor} disabled={!projectLoaded}>
          <IconCursor className="size-4" />
          Open in Code Editor
        </DropdownMenuItem>
        {editorOpened && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuSub>
              <DropdownMenuSubTrigger>View</DropdownMenuSubTrigger>
              <DropdownMenuSubContent>
                <DropdownMenuItem onClick={() => handleReload(true)}>
                  <IconReload className="size-4" />
                  Reload
                  <DropdownMenuShortcut>{ctrlOrCmd}+R</DropdownMenuShortcut>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleToggleFullScreen}>
                  <MaximizeIcon className="size-4" />
                  Toggle Full Screen
                  <DropdownMenuShortcut>F11</DropdownMenuShortcut>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => editorStore.resetLayout()}>
                  <LayoutGridIcon className="size-4" />
                  Reset Layout
                </DropdownMenuItem>
              </DropdownMenuSubContent>
            </DropdownMenuSub>
            {sdk && (
              <>
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger>Help</DropdownMenuSubTrigger>
                  <DropdownMenuSubContent>
                    <DropdownMenuItem onClick={handleToggleDevConsole}>
                      <IconDevTools className="size-4" />
                      Toggle Dev Console
                      <DropdownMenuShortcut>F12</DropdownMenuShortcut>
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handleOpenAppLog}>
                      <IconFolder1 className="size-4" />
                      Open App Log
                    </DropdownMenuItem>
                    {appInfo?.isDev && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => openDialog('loadUrl')}>
                          <WrenchIcon className="size-4" />
                          Dev Load URL
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                      </>
                    )}
                    <DropdownMenuItem onClick={() => openDialog('whatsNew')}>
                      <NewspaperIcon className="size-4" />
                      Changelog
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handleCheckForUpdates}>
                      <IconReload className="size-4" />
                      Check for Updates
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handleShowAbout}>
                      <IconHelp className="size-4" />
                      About {PRODUCT_NAME}{appInfo?.isDev ? ' DEV' : ''}
                    </DropdownMenuItem>
                  </DropdownMenuSubContent>
                </DropdownMenuSub>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => sdk?.window.close()}>
                  <LogOutIcon className="size-4" />
                  Quit {PRODUCT_NAME}{appInfo?.isDev ? ' DEV' : ''}
                  <DropdownMenuShortcut>{ctrlOrCmd}+Q</DropdownMenuShortcut>
                </DropdownMenuItem>
              </>
            )}
          </>
        )}
      </DropdownMenuGroup>
    </>
  ), [editorOpened, handleNewScene, handleSave, handleSaveAs, handleClearThumbnails, isEditorBusy, firebaseUser, projectLoaded, buildingProject, isPlayMode, isPlayModeStarting, isEditorDisabled, isAuthRequired, ctrlOrCmd, openDialog, handleShowInExplorer, handleOpenInCodeEditor, handleReload, handleToggleFullScreen, handleToggleDevConsole, handleOpenAppLog, handleCheckForUpdates, handleShowAbout, appSettingsMenu, thumbnailService]);

  const viewMenuItems = useMemo(() => (
    <>
      <DropdownMenuGroupLabel>View</DropdownMenuGroupLabel>
      <DropdownMenuGroup>
        <DropdownMenuItem onClick={() => handleReload(true)}>
          <IconReload className="size-4" />
          Reload
          <DropdownMenuShortcut>{ctrlOrCmd}+R</DropdownMenuShortcut>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handleToggleFullScreen}>
          <MaximizeIcon className="size-4" />
          Toggle Full Screen
          <DropdownMenuShortcut>F11</DropdownMenuShortcut>
        </DropdownMenuItem>
        {sdk && appSettingsMenu}
        <DropdownMenuSeparator />
        <DropdownMenuGroupLabel>Help</DropdownMenuGroupLabel>
        <DropdownMenuItem onClick={handleToggleDevConsole}>
          <IconDevTools className="size-4" />
          Toggle Dev Console
          <DropdownMenuShortcut>F12</DropdownMenuShortcut>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handleOpenAppLog}>
          <IconFolder1 className="size-4" />
          Open App Log
        </DropdownMenuItem>
        {appInfo?.isDev && (
          <DropdownMenuItem onClick={() => openDialog('loadUrl')}>
            <WrenchIcon className="size-4" />
            Dev Load URL
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onClick={() => openDialog('whatsNew')}>
          <NewspaperIcon className="size-4" />
          Changelog
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handleCheckForUpdates}>
          <IconReload className="size-4" />
          Check for Updates
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handleShowAbout}>
          <IconHelp className="size-4" />
          About {PRODUCT_NAME}{appInfo?.isDev ? ' DEV' : ''}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => sdk?.window.close()}>
          <LogOutIcon className="size-4" />
          Quit {PRODUCT_NAME}{appInfo?.isDev ? ' DEV' : ''}
          <DropdownMenuShortcut>{ctrlOrCmd}+Q</DropdownMenuShortcut>
        </DropdownMenuItem>
      </DropdownMenuGroup>
    </>
  ), [ctrlOrCmd, handleReload, handleToggleFullScreen, handleToggleDevConsole, handleOpenAppLog, handleCheckForUpdates, handleShowAbout, openDialog, appSettingsMenu]);

  const webReloadMenuItems = useMemo(() => (
    <>
      <DropdownMenuGroupLabel>View</DropdownMenuGroupLabel>
      <DropdownMenuGroup>
        <DropdownMenuItem onClick={() => handleReload(true)}>
          <IconReload className="size-4" />
          Reload
          <DropdownMenuShortcut>{ctrlOrCmd}+R</DropdownMenuShortcut>
        </DropdownMenuItem>
      </DropdownMenuGroup>
    </>
  ), [ctrlOrCmd, handleReload]);

  return (
    <>
      <DropdownMenu open={isMenuOpen} onOpenChange={setIsMenuOpen}>
        <DropdownMenuTrigger asChild className="titlebarControls">
          <TitleBarButton tooltip="Menu" pressed={isMenuOpen}>
            <IconMenu className="size-4" />
          </TitleBarButton>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-56" align="start">
          {editorOpened && projectMenuItems}
          {!editorOpened && (sdk ? viewMenuItems : webReloadMenuItems)}
        </DropdownMenuContent>
      </DropdownMenu>

      <UploadProjectDialog
        open={store.isUploadProjectDialogOpen}
        onOpenChange={open => store.setUploadProjectDialogOpen(open)}
      />
      <ModelViewerDialog
        open={store.isModelViewerDialogOpen}
        onOpenChange={open => store.setModelViewerDialogOpen(open)}
        asset={store.modelViewerAsset}
      />
      <ProjectSettingsDialog
        isOpen={dialogs.projectSettings}
        onOpenChange={open => !open && closeDialog('projectSettings')}
      />
      <SaveSceneDialog
        isOpen={saveDialogMode !== null}
        onOpenChange={open => !open && setSaveDialogMode(null)}
        onSave={handleSaveDialogSubmit}
        mode={saveDialogMode === 'saveAs' ? 'saveAs' : 'new'}
      />
      <NavMeshDialog
        isOpen={dialogs.navMesh}
        onOpenChange={open => !open && closeDialog('navMesh')}
      />
      <BrowseAssetPacksDialog
        open={dialogs.browseAssetPacks}
        onOpenChange={open => !open && closeDialog('browseAssetPacks')}
        targetProjectPath={project.projectFolder}
      />
      <NewAssetPackDialog
        open={dialogs.newAssetPack}
        onOpenChange={open => !open && closeDialog('newAssetPack')}
        targetProjectPath={project.projectFolder}
      />
      <ExportAssetPackDialog
        open={dialogs.exportAssetPack}
        onOpenChange={open => !open && closeDialog('exportAssetPack')}
        targetProjectPath={project.projectFolder}
      />
      <LoadUrlDialog
        isOpen={dialogs.loadUrl}
        onOpenChange={open => !open && closeDialog('loadUrl')}
        onOpen={handleDevLoadUrlSubmit}
      />
      <AboutDialog
        isOpen={dialogs.about}
        onOpenChange={open => !open && closeDialog('about')}
        appInfo={aboutAppInfo}
      />
      <WhatsNewDialog
        mode="changelog"
        isOpen={dialogs.whatsNew}
        onOpenChange={open => !open && closeDialog('whatsNew')}
        currentVersion={EDITOR_VERSION}
      />
    </>
  );
});
TitleBarMenu.displayName = 'TitleBarMenu';
